import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';
import { toast } from 'sonner';
import CsvImportDialog from './CsvImportDialog';

/**
 * "CSV ▾" dropdown — Export, Download Template, Import.
 *
 * Props:
 *   exportPath      — e.g. "/inventory/export.csv"
 *   templatePath    — e.g. "/inventory/import-template.csv"
 *   importPath      — e.g. "/inventory/import"
 *   kind            — "inventory" | "logs"
 *   dialogTitle     — passed through to CsvImportDialog
 *   dialogDescription
 *   onImported      — refresh callback after a successful import
 *   canImport       — when false, the Import item is hidden (e.g. non-admin)
 *   testIdPrefix    — e.g. "inv" → "inv-csv-menu-btn"
 */
const CsvMenu = ({
  exportPath,
  templatePath,
  importPath,
  kind,
  dialogTitle,
  dialogDescription,
  onImported,
  canImport = true,
  testIdPrefix = 'csv',
}) => {
  const [importOpen, setImportOpen] = useState(false);

  // Auth-protected downloads — fetch as blob, save via temp anchor.
  const downloadCsv = async (path, defaultName) => {
    try {
      const res = await axios.get(`${API}${path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      // Prefer the server-supplied filename if present.
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : defaultName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" data-testid={`${testIdPrefix}-csv-menu-btn`}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            CSV
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem
            onSelect={() => downloadCsv(exportPath, `${kind}-export.csv`)}
            data-testid={`${testIdPrefix}-csv-export-btn`}
          >
            <Download className="mr-2 h-4 w-4" />
            Export current data
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => downloadCsv(templatePath, `${kind}-template.csv`)}
            data-testid={`${testIdPrefix}-csv-template-btn`}
          >
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Download import template
          </DropdownMenuItem>
          {canImport && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setImportOpen(true)}
                data-testid={`${testIdPrefix}-csv-import-btn`}
              >
                <Upload className="mr-2 h-4 w-4" />
                Import from CSV…
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {canImport && (
        <CsvImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onApplied={onImported}
          title={dialogTitle}
          description={dialogDescription}
          importPath={importPath}
          kind={kind}
        />
      )}
    </>
  );
};

export default CsvMenu;
